// Barcode generation and validation utilities
// Supports multiple barcode formats: CODE128, CODE39, EAN13, UPC-A, QR Code

export type BarcodeFormat = 'CODE128' | 'CODE39' | 'EAN13' | 'UPCA' | 'QR';

export interface BarcodeOptions {
  format: BarcodeFormat;
  width?: number;
  height?: number;
  displayValue?: boolean;
  fontSize?: number;
  margin?: number;
  background?: string;
  lineColor?: string;
}

// Common barcode patterns
const PATTERNS = {
  // Code 128 character sets
  CODE128_START_B: [2, 1, 1, 2],
  CODE128_STOP: [2, 3, 3, 1],
  CODE128_PATTERNS: [
    '11011001100', '11001101100', '11001100110', '10010011000', '10010001100',
    '10001001100', '10011001000', '10011000100', '10001100100', '11001001000',
    '11001000100', '11000100100', '10110011100', '10011011100', '10011001110',
    '10111001100', '10011101100', '10011100110', '11001110010', '11001011100',
    '11001001110', '11011100100', '11001110100', '11101101110', '11101001100',
    '11100101100', '11100100110', '11101100100', '11100110100', '11100110010',
    '11011011000', '11011000110', '11000110110', '10100011000', '10001011000',
    '10001000110', '10110001000', '10001101000', '10001100010', '11010001000',
    '11000101000', '11000100010', '10110111000', '10110001110', '10001101110',
    '10111011000', '10111000110', '10001110110', '11101110110', '11010001110',
    '11000101110', '11011101000', '11011100010', '11011101110', '11101011000',
    '11101000110', '11100010110', '11101101000', '11101100010', '11100011010',
    '11101111010', '11001000010', '11110001010', '10100110000', '10100001100',
    '10010110000', '10010001100', '10000101100', '10000100110', '10110010000',
    '10110000100', '10011010000', '10011000010', '10000110100', '10000110010',
    '11000010010', '11001010000', '11110111010', '11000010100', '10001111010',
    '10100111100', '10010111100', '10010011110', '10111100100', '10011110100',
    '10011110010', '11110100100', '11110010100', '11110010010', '11011011110',
    '11011110110', '11110110110', '10101111000', '10100011110', '10001011110',
    '10111101000', '10111100010', '11110101000', '11110100010', '10111011110',
    '10111101110', '11101011110', '11110101110', '11010011100', '11010000110',
    '11000111100', '11000110010', '10001111010', '10001100010', '10011011010',
    '10011101010', '11110111110', '11001001110', '11011110110', '11101001110',
  ],

  // EAN-13 check digit calculation
  EAN13_WEIGHTS: [1, 3, 1, 3, 1, 3, 1, 3, 1, 3, 1, 3],
};

// Generate CODE128 barcode pattern
export function generateCODE128(data: string): string {
  const values: number[] = [];
  
  // Start with Code B
  values.push(104); // Code B start
  
  for (const char of data) {
    const code = char.charCodeAt(0) - 32;
    if (code >= 0 && code <= 95) {
      values.push(code);
    } else {
      throw new Error(`Invalid character for CODE128: ${char}`);
    }
  }
  
  // Calculate checksum
  let checksum = 104; // Start B
  values.forEach((v, i) => {
    checksum += v * (i + 1);
  });
  values.push(checksum % 103);
  
  // Stop pattern
  values.push(106);
  
  return values.map(v => PATTERNS.CODE128_PATTERNS[v] || '').join('');
}

// Generate EAN-13 barcode
export function generateEAN13(data: string): string {
  if (data.length !== 12) {
    throw new Error('EAN-13 requires exactly 12 digits');
  }
  
  // Validate digits
  if (!/^\d{12}$/.test(data)) {
    throw new Error('EAN-13 must contain only digits');
  }
  
  // Calculate check digit
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(data[i]) * PATTERNS.EAN13_WEIGHTS[i];
  }
  const checkDigit = (10 - (sum % 10)) % 10;
  const fullCode = data + checkDigit;
  
  // Generate pattern (simplified - actual EAN-13 encoding is more complex)
  return `start_${fullCode}_end`;
}

// Generate QR Code (placeholder - would need qrcode library)
export function generateQRData(data: string): { data: string; size: number } {
  return {
    data: data,
    size: Math.max(100, Math.min(300, data.length * 5)),
  };
}

// Validate barcode data
export function validateBarcode(data: string, format: BarcodeFormat): { valid: boolean; error?: string } {
  switch (format) {
    case 'EAN13':
      if (!/^\d{12}$/.test(data)) {
        return { valid: false, error: 'EAN-13 requires exactly 12 digits' };
      }
      break;
    case 'UPCA':
      if (!/^\d{11}$/.test(data)) {
        return { valid: false, error: 'UPC-A requires exactly 11 digits' };
      }
      break;
    case 'CODE39':
      if (!/^[A-Z0-9\-\.\s\$\/\+\%]+$/.test(data)) {
        return { valid: false, error: 'CODE39 allows only uppercase letters, digits, and -.$/+%' };
      }
      break;
    case 'CODE128':
      if (!/^[\x00-\x7F]+$/.test(data)) {
        return { valid: false, error: 'CODE128 allows only ASCII characters' };
      }
      break;
  }
  return { valid: true };
}

// Format barcode for display
export function formatBarcodeDisplay(data: string, format: BarcodeFormat): string {
  switch (format) {
    case 'EAN13':
    case 'UPCA':
      // Add spaces for readability
      return data.match(/.{1,4}/g)?.join(' ') || data;
    case 'CODE128':
    case 'CODE39':
      return data.toUpperCase();
    default:
      return data;
  }
}

// Generate human-readable barcode number for items
export function generateItemBarcode(
  itemCode: string,
  batchNumber?: string,
  locationCode?: string
): string {
  const parts = [itemCode];
  
  if (batchNumber) {
    parts.push(`B${batchNumber}`);
  }
  
  if (locationCode) {
    parts.push(`L${locationCode}`);
  }
  
  // Use CODE128 format for variable-length data
  return parts.join('-');
}

// Parse item barcode
export function parseItemBarcode(
  barcode: string
): { itemCode: string; batchNumber?: string; locationCode?: string } {
  const parts = barcode.split('-');
  
  return {
    itemCode: parts[0] || '',
    batchNumber: parts.find(p => p.startsWith('B'))?.substring(1),
    locationCode: parts.find(p => p.startsWith('L'))?.substring(1),
  };
}

// Generate PO barcode
export function generatePOBarcode(poNumber: string): string {
  return `PO-${poNumber.replace(/[^A-Z0-9]/gi, '')}`;
}

// Generate return/RMA barcode
export function generateRMABarcode(rmaNumber: string): string {
  return `RMA-${rmaNumber.replace(/[^A-Z0-9]/gi, '')}`;
}

// Generate location barcode
export function generateLocationBarcode(locationCode: string): string {
  return `LOC-${locationCode}`;
}

// SVG barcode renderer (simple implementation)
export function renderBarcodeSVG(
  data: string,
  options: BarcodeOptions
): string {
  const {
    width = 200,
    height = 80,
    displayValue = true,
    fontSize = 14,
    margin = 10,
    background = '#ffffff',
    lineColor = '#000000',
  } = options;

  // For CODE128, generate the pattern
  let barcodePattern = '';
  let displayText = data;

  try {
    if (options.format === 'CODE128') {
      barcodePattern = generateCODE128(data);
    } else if (options.format === 'EAN13') {
      barcodePattern = generateEAN13(data);
    } else if (options.format === 'QR') {
      const qrData = generateQRData(data);
      return `<svg width="${qrData.size}" height="${qrData.size}" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="${background}"/>
        <text x="50%" y="50%" text-anchor="middle" font-size="12">QR: ${data.substring(0, 20)}...</text>
      </svg>`;
    }
  } catch {
    barcodePattern = '11011001100'; // Fallback pattern
  }

  const barWidth = (width - margin * 2) / barcodePattern.length;
  const barHeight = displayValue ? height - fontSize - margin : height - margin * 2;

  let svg = `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
    <rect width="100%" height="100%" fill="${background}"/>`;

  // Draw bars
  let x = margin;
  for (const bit of barcodePattern) {
    if (bit === '1') {
      svg += `<rect x="${x}" y="${margin}" width="${barWidth}" height="${barHeight}" fill="${lineColor}"/>`;
    }
    x += barWidth;
  }

  // Draw text
  if (displayValue) {
    svg += `<text x="${width / 2}" y="${height - 2}" text-anchor="middle" font-size="${fontSize}" font-family="monospace" fill="${lineColor}">${displayText}</text>`;
  }

  svg += '</svg>';
  return svg;
}

// Export for use in components
export const barcodeUtils = {
  generateCODE128,
  generateEAN13,
  generateQRData,
  validateBarcode,
  formatBarcodeDisplay,
  generateItemBarcode,
  parseItemBarcode,
  generatePOBarcode,
  generateRMABarcode,
  generateLocationBarcode,
  renderBarcodeSVG,
};
